import { inject, Injectable } from '@angular/core';
import { LoadingController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap, tap } from 'rxjs';

import { LanguageSelectors, NavigationService, NotificationActions } from '@ninety/mobile/platform/state';

import { DiscussionType } from '../../models/discussion.models';
import { SaveDiscussionFormDto, SaveDiscussionFormDtoFactory } from '../../models/dto/save-discussion-form.dto';
import { DiscussionFormMode } from '../../models/form/discussion-form.model';
import { DiscussionApiService } from '../../services/discussion-api.service';
import { DiscussionFormComponentActions, DiscussionFormEffectsActions } from '../actions';
import { DiscussionFormSelectors, selectActiveDiscussionId } from '../selectors';

@Injectable()
export class DiscussionFormEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly api = inject(DiscussionApiService);
  private readonly nav = inject(NavigationService);
  private readonly loadingCtrl = inject(LoadingController);

  private readonly discussion$ = this.store.select(DiscussionFormSelectors.selectFormDiscussion);
  private readonly mode$ = this.store.select(DiscussionFormSelectors.selectFormMode);

  requestDiscussionOn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormComponentActions.discussionFormInit),
      map(() => DiscussionFormEffectsActions.discussionRequested())
    )
  );

  requestDiscussion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormEffectsActions.discussionRequested),
      concatLatestFrom(() => this.store.select(selectActiveDiscussionId)),
      switchMap(([, id]) =>
        this.api.getDiscussion(id).pipe(
          switchMap(d =>
            this.api.attachDiscussionTodoStats(d).pipe(
              map(discussion => DiscussionFormEffectsActions.discussionRequestSucceeded({ discussion })),
              catchError(() => of(DiscussionFormEffectsActions.discussionRequestFailed()))
            )
          ),
          catchError(() => of(DiscussionFormEffectsActions.discussionRequestFailed()))
        )
      )
    )
  );

  notifyIfNotParticipant = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormEffectsActions.discussionRequestSucceeded),
      concatLatestFrom(() => this.store.select(DiscussionFormSelectors.selectFormMode)),
      filter(([, mode]) => mode !== DiscussionFormMode.Manager && mode !== DiscussionFormMode.Managee),
      tap(() => {
        this.backToList();
        this.loadingCtrl.dismiss();
      }),
      map(() =>
        NotificationActions.showToast({
          options: { message: 'You must be a participant to view this form.', type: 'negative' },
        })
      )
    )
  );

  saveDiscussion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormComponentActions.discussionFormSaved),
      concatLatestFrom(() => [this.discussion$, this.mode$]),
      map(([payload, discussion, mode]) => {
        if (discussion && (mode === DiscussionFormMode.Manager || mode === DiscussionFormMode.Managee)) {
          return {
            dto: SaveDiscussionFormDtoFactory.create({ ...payload, discussion, mode }),
            finished: payload.finished,
            discussionType: discussion.type,
          };
        }
        return null;
      }),
      filter(val => !!val?.dto),
      switchMap(val => {
        const dto = val?.dto as SaveDiscussionFormDto;
        return this.api.saveForm(dto._id, dto).pipe(
          map(() =>
            DiscussionFormEffectsActions.discussionFormSaveSucceeded({
              finished: !!val?.finished,
              _id: dto._id,
              discussionType: val?.discussionType as DiscussionType,
            })
          ),
          catchError(e => of(DiscussionFormEffectsActions.discussionFormSaveFailed()))
        );
      })
    )
  );

  redirectWhenFinished = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DiscussionFormEffectsActions.discussionFormSaveSucceeded),
        filter(a => a.finished),
        tap(() => this.backToList())
      ),
    { dispatch: false }
  );

  dismissLoading = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          DiscussionFormEffectsActions.discussionRequestFailed,
          DiscussionFormEffectsActions.discussionFormSaveFailed,
          DiscussionFormEffectsActions.discussionFormSaveSucceeded
        ),
        tap(() => this.loadingCtrl.dismiss())
      ),
    { dispatch: false }
  );

  showToastOnSaveSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormEffectsActions.discussionFormSaveSucceeded),
      map(({ finished }) =>
        NotificationActions.showToast({
          options: {
            message: finished ? 'Form completed' : 'Form saved',
            duration: 500,
          },
        })
      )
    )
  );

  handleRequestError = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormEffectsActions.discussionRequestFailed),
      concatLatestFrom(() => this.store.select(LanguageSelectors.discussionLanguage)),
      tap(() => this.backToList()),
      map(([, lang]) =>
        NotificationActions.showToast({
          options: {
            type: 'negative',
            message: `Failed to load ${lang.item}. Please try again.`,
          },
        })
      )
    )
  );

  handleSaveError = createEffect(() =>
    this.actions$.pipe(
      ofType(DiscussionFormEffectsActions.discussionFormSaveFailed),
      concatLatestFrom(() => this.store.select(LanguageSelectors.discussionLanguage)),
      map(([, lang]) =>
        NotificationActions.showToast({
          options: {
            type: 'negative',
            message: `Failed to save ${lang.item}. Please try again.`,
          },
        })
      )
    )
  );

  private backToList() {
    this.nav.goToDiscussions();
  }
}
